import { CarrierVisitStatus } from '@planning/app/api'
import { CarrierVisitStatusChip } from '@planning/components/CarrierVisitStatusChip'
import { emptyIndicator } from '@planning/constants'
import { columnFilterPresets } from '@planning/pages/VesselVisit/tableConfigs/columnFilterConfigs'
import { getDateAndLabel } from '@planning/pages/VesselVisit/tableConfigs/vesselVisitListColumns'
import { IRailVisitItem } from '@planning/rt-stores/railVisit/RailVisitItem'
import { useTranslate } from '@tolgee/react'
import {
  getMultiSelectRenderBadge,
  TitleWithCaptionCell,
  tomTableColumnFilterPresets,
} from '@tom-ui/ui'
import { MRT_ColumnDef, MRT_Row } from 'material-react-table'
import { useMemo } from 'react'

export interface RailVisitTableDto {
  id: number
  name: string
  status: CarrierVisitStatus
  cargoType: string
  inboundTripIds: string
  outboundTripIds: string
  eta?: string | null
  ata?: string | null
  etd?: string | null
  atd?: string | null
  tracks: string
  moves: number
  originalRef: IRailVisitItem
}

export const useRailVisitTableColumns = () => {
  const { t } = useTranslate()

  const columns = useMemo<MRT_ColumnDef<RailVisitTableDto>[]>(
    () => [
      {
        id: 'name',
        header: t('name', 'Name'),
        size: 170,
        accessorFn: row => row.name,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'status',
        header: t('status', 'Status'),
        size: 170,
        accessorFn: row => row.status,
        Cell: ({ row }: { row: MRT_Row<RailVisitTableDto> }) => (
          <CarrierVisitStatusChip status={row.original.status} />
        ),
        muiFilterTextFieldProps: ({ column }) =>
          getMultiSelectRenderBadge(column, t, t('filterByStatus', 'Filter by Status')),
        ...columnFilterPresets.vesselVisitStatusMultiSelect(),
      },
      {
        id: 'cargoType',
        header: t('type', 'Type'),
        size: 100,
        accessorFn: row => row.cargoType,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'inboundTripIds',
        header: t('inId', 'In. ID'),
        accessorFn: row => row.inboundTripIds,
        Cell: ({ cell }) => cell.getValue<string>() || emptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        id: 'outboundTripIds',
        header: t('outId', 'Out. ID'),
        accessorFn: row => row.outboundTripIds,
        Cell: ({ cell }) => cell.getValue<string>() || emptyIndicator,
        ...tomTableColumnFilterPresets.searchOptional(),
      },
      {
        ...tomTableColumnFilterPresets.dateMandatory(),
        id: 'etaAta',
        accessorFn: row => row.originalRef.arrival,
        header: t('etaAta', 'ETA / ATA'),
        size: 160,
        Cell: ({ row }: { row: MRT_Row<RailVisitTableDto> }) => {
          const { date, caption } = getDateAndLabel(
            row.original.ata,
            row.original.eta,
            'ATA',
            'ETA',
          )
          return <TitleWithCaptionCell title={date} caption={caption} />
        },
        enableGlobalFilter: false,
      },
      {
        ...tomTableColumnFilterPresets.dateMandatory(),
        id: 'etdAtd',
        accessorFn: row => row.originalRef.departure,
        header: t('etdAtd', 'ETD / ATD'),
        size: 160,
        Cell: ({ row }: { row: MRT_Row<RailVisitTableDto> }) => {
          const { date, caption } = getDateAndLabel(
            row.original.atd,
            row.original.etd,
            'ATD',
            'ETD',
          )
          return <TitleWithCaptionCell title={date} caption={caption} />
        },
        enableGlobalFilter: false,
      },
      {
        id: 'tracks',
        header: t('tracks', 'Tracks'),
        accessorFn: row => row.tracks,
        Cell: ({ cell }) => cell.getValue<string>() || emptyIndicator,
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        id: 'moves',
        header: t('moves', 'Moves'),
        accessorFn: row => row.moves,
        ...tomTableColumnFilterPresets.numericMandatory(),
      },
    ],
    [t],
  )

  return columns
}
